/*
 * Jira Ticket:
 * Created Date: Thu, 12th Mar 2020, 14:25:08 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { useState, useCallback } from 'react';
import {
  List,
  Title,
  Confirm,
  Datagrid,
  ChipField,
  TextField,
  useNotify,
  ArrayField,
  useRefresh,
  useGetList,
  useRedirect,
  SelectField,
  useTranslate,
  FunctionField,
  SingleFieldList,
  useDataProvider,
} from 'react-admin';
import { EmptyResource } from 'Layout';
import ContractorFilters from './filter';
import ContractorActions from './actions';
import { actionButtonStyles } from 'styles';
import { subcontractorStatusOptions } from 'utils/choices';
import { PostPagination } from '../../Components/CustomPagination/pagination';
import { usePersistentOrganisation } from '../../Components/Contexts';
import { ThumbUp, ThumbDown, Delete } from '@material-ui/icons';
import { ActionButton } from 'Components/Buttons';

const Contract = props => {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const buttonClasses = actionButtonStyles();
  const {
    persistentOrganisation,
    onSelectPersistentOrganisation,
  } = usePersistentOrganisation();

  const [dataToSubmit, setDataToSubmit] = useState({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { total, loading, error } = useGetList('Organisation', {
    page: 1,
    perPage: 1,
  });
  const hasOrganisations = !loading && !error && total > 0;

  const EmptyContractsCallback = useCallback(() => {
    const EmptyContracts = () => {
      const title = hasOrganisations
        ? 'resources.Contract.noContractsFound'
        : 'resources.Contract.emptyStateTitle';
      const subtitle = hasOrganisations
        ? ''
        : 'resources.Contract.emptyStateSubtitle';
      const buttonLabel = 'createOrganisation';
      const basePath = hasOrganisations ? null : '/Organisation';
      return (
        <EmptyResource
          title={title}
          subtitle={subtitle}
          buttonLabel={buttonLabel}
          basePath={basePath}
        />
      );
    };
    return EmptyContracts();
  }, [hasOrganisations, redirect]);

  const handleActionButton = ({ action, record }) => {
    setDataToSubmit({
      action,
      id: record.id,
      invitingOrg: record.subcontractor.name,
    });
    return setShowConfirmModal(true);
  };

  const handleUpdateContract = ({ action, id }) => {
    if (action) {
      return dataProvider(action, 'Subcontract', { id })
        .then(result => {
          refresh();
          setShowConfirmModal(false);
          return notify('notification.contract.updateSuccess', 'info');
        })
        .catch(error => {
          setShowConfirmModal(false);
          const {
            extensions: { code },
            message,
          } = error.graphQLErrors[0];
          if (code !== 'INTERNAL_SERVER_ERROR') {
            return notify(message, 'warning');
          }
          return notify('error.generic', 'warning');
        });
    }
  };

  return (
    <>
      <Title title={`resources.General.cmsTitle`} />
      {hasOrganisations ? (
        <List
          filterDefaultValues={
            persistentOrganisation.id
              ? {
                  subcontractorId: persistentOrganisation.id,
                }
              : {
                  isSubcontractors: false,
                }
          }
          empty={<EmptyContractsCallback />}
          title={translate('resources.Subcontractor.name', { smart_count: 2 })}
          bulkActionButtons={false}
          perPage={25}
          pagination={<PostPagination />}
          actions={
            <ContractorActions
              exporter={null}
              organisationId={persistentOrganisation.id}
            />
          }
          filters={
            <ContractorFilters
              organisationId={persistentOrganisation.id}
              organisationFilter={onSelectPersistentOrganisation}
            />
          }
          {...props}
          hasCreate={true} // lie so List will use the `empty` component
        >
          <Datagrid>
            <TextField
              source="subcontractor.name"
              label={'resources.Subcontractor.fields.invitedOrg'}
              sortable={false}
            />
            <TextField
              source="organisation.name"
              label={'resources.Subcontractor.fields.invitingOrg'}
              sortable={false}
            />
            <ArrayField
              source="projects"
              sortable={false}
              label={'resources.Subcontractor.fields.projects'}
            >
              <SingleFieldList linkType={false}>
                <ChipField source="name" />
              </SingleFieldList>
            </ArrayField>
            <SelectField
              source="status"
              choices={subcontractorStatusOptions}
              label={'resources.Subcontractor.fields.status'}
              sortable={false}
            />
            {persistentOrganisation.id && (
              <FunctionField
                render={record =>
                  record.status === 'PENDING' ? (
                    <ActionButton
                      icon={<ThumbUp />}
                      name={'actions.accept'}
                      onClick={() =>
                        handleActionButton({ action: 'ACCEPT', record })
                      }
                      className={buttonClasses.approve}
                    />
                  ) : null
                }
              />
            )}
            {persistentOrganisation.id && (
              <FunctionField
                render={record => (
                  <ActionButton
                    icon={
                      record.status === 'PENDING' ? <ThumbDown /> : <Delete />
                    }
                    name={
                      record.status === 'PENDING'
                        ? 'actions.reject'
                        : 'actions.revoke'
                    }
                    onClick={() =>
                      handleActionButton({
                        action:
                          record.status === 'PENDING' ? 'REJECT' : 'REVOKE',
                        record,
                      })
                    }
                    className={buttonClasses.decline}
                  />
                )}
              />
            )}
          </Datagrid>
        </List>
      ) : (
        EmptyContractsCallback()
      )}
      <Confirm
        isOpen={showConfirmModal}
        title={translate(
          'resources.Subcontractor.fields.updateContractModalTitle',
          {
            action:
              dataToSubmit.action &&
              dataToSubmit.action.charAt(0).toUpperCase() +
                dataToSubmit.action.substr(1).toLowerCase(),
          }
        )}
        content={translate(
          'resources.Subcontractor.fields.updateContractModalContent',
          {
            invitingOrgName: dataToSubmit && dataToSubmit.invitingOrg,
            action: dataToSubmit.action && dataToSubmit.action.toLowerCase(),
          }
        )}
        onConfirm={() => handleUpdateContract(dataToSubmit)}
        onClose={() => setShowConfirmModal(false)}
      />
    </>
  );
};

export default Contract;
